import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiHeart,
  FiShoppingCart,
  FiShoppingBag,
  FiUser,
  FiMenu,
  FiX,
  FiSearch,
  FiLogOut,
} from "react-icons/fi";
import { MAIN_CATEGORY_ROUTES } from "../lib/categoryConfig";
import { useAuth } from "../contexts/AuthContext";
import { useCartWishlist } from "../contexts/CartWishlistContext";
import { searchProducts } from "../lib/productService";
import "./Navbar.css";
import logo from "../assets/logo.png";

// ✅ Popup page component (in src/pages)
import AuthPopup from "../pages/AuthPopup";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // ✅ Auth popup state
  const [authOpen, setAuthOpen] = useState(false);
  const { user, signOut } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile menu + mobile search on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  // Prevent background scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // ✅ Prevent background scroll when auth popup open
  useEffect(() => {
    if (!authOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [authOpen]);

  const linkClass = ({ isActive }) => (isActive ? "active" : "");

  // Nav categories: always show Jewellery, Bags & Purses, Traditional & Western Wear
  const navCategories = useMemo(() => Object.keys(MAIN_CATEGORY_ROUTES).map(name => ({
    name,
    slug: (MAIN_CATEGORY_ROUTES[name] || "").replace(/^\//, "") || name.toLowerCase().replace(/\s+&\s+/g, "-").replace(/\s+/g, "-")
  })), []);

  // ✅ Cart & Wishlist from Supabase (via context)
  const { cart, wishlist } = useCartWishlist();
  const cartCount = cart.reduce((s, it) => s + (it.qty || 1), 0);
  const wishCount = wishlist.length;

  // ===================== ✅ SEARCH (shared) =====================
  const placeholderItems = useMemo(
    () => [
      "Necklaces",
      "Bracelets",
      "Bangles",
      "Pendants",
      "Earrings",
      "Chokers",
      "Traditional Wear",
      "Western Wear",
      "Kurtis",
      "Sarees",
      "Bags",
      "Clutches",
      "Purses",
    ],
    []
  );

  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (!q || q.trim().length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      const res = await searchProducts(q);
      setSearchResults(res);
      setIsSearching(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((p) => (p + 1) % placeholderItems.length);
      setAnimKey((k) => k + 1);
    }, 2000);
    return () => clearInterval(t);
  }, [placeholderItems.length]);

  const routeForQuery = (text) => {
    const s = (text || "").toLowerCase().trim();
    if (!s) return null;

    const bagWords = [
      "bag",
      "bags",
      "purse",
      "purses",
      "handbag",
      "hand bag",
      "tote",
      "totebag",
      "sling",
      "crossbody",
      "cross body",
      "clutch",
      "satchel",
      "shoulder bag",
      "shoulder",
    ];
    if (bagWords.some((w) => s.includes(w))) return { path: "/bags-purses" };

    const clothingWords = [
      "traditional",
      "western",
      "ethnic",
      "wear",
      "dress",
      "dresses",
      "kurti",
      "kurtis",
      "kurthi",
      "kurthis",
      "kurta",
      "kurtas",
      "salwar",
      "suit",
      "suits",
      "anarkali",
      "straight cut",
      "punjabi",
      "patiala",
      "lehenga",
      "saree",
      "sarees",
      "gown",
      "gowns",
      "frock",
      "frocks",
      "top",
      "tops",
      "jeans",
      "skirt",
      "co-ord",
      "coord",
      "co ord",
      "maxi",
      "denim",
      "korean",
    ];

    if (clothingWords.some((w) => s.includes(w))) {
      const isWestern = [
        "western",
        "denim",
        "korean",
        "frock",
        "gown",
        "dress",
        "tops",
        "jeans",
        "skirt",
        "maxi",
      ].some((w) => s.includes(w));

      return {
        path: isWestern
          ? "/traditional-western?mode=western"
          : "/traditional-western?mode=traditional",
      };
    }

    const toCat = (cat) => ({ path: `/jewellery?cat=${cat}` });

    if (s.includes("bracelet") || s.includes("kada") || s.includes("cuff"))
      return toCat("bracelets");
    if (s.includes("bangle")) return toCat("bangles");
    if (
      s.includes("earring") ||
      s.includes("stud") ||
      s.includes("hoop") ||
      s.includes("monalisa")
    )
      return toCat("earrings");
    if (s.includes("ring")) return toCat("rings");
    if (
      s.includes("pendant") ||
      s.includes("necklace") ||
      s.includes("choker") ||
      s.includes("set") ||
      s.includes("layered")
    )
      return toCat("necklace");

    return { path: "/jewellery" };
  };

  const mobileSearchRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (!mobileSearchOpen) return;
      if (!mobileSearchRef.current) return;
      if (!mobileSearchRef.current.contains(e.target)) {
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [mobileSearchOpen]);

  useEffect(() => {
    if (mobileSearchOpen) {
      setTimeout(() => mobileInputRef.current?.focus(), 80);
    }
  }, [mobileSearchOpen]);

  const handleSearch = () => {
    const target = routeForQuery(q);
    if (!target) return;
    navigate(target.path);
    setQ("");
    setMobileSearchOpen(false);
    setMobileOpen(false);
  };

  // ✅ Open/Close auth popup
  const openAuth = () => {
    setAuthOpen(true);
    setMobileOpen(false);
    setMobileSearchOpen(false);
  };
  const closeAuth = () => setAuthOpen(false);

  // Reusable dropdown renderer
  const renderSearchDropdown = () => {
    if (!q || q.trim().length === 0 || !searchFocused) return null;

    return (
      <div className="searchDropdown" onMouseDown={(e) => e.preventDefault()}>
        {isSearching ? (
          <div className="searchDropMsg">Searching...</div>
        ) : searchResults.length > 0 ? (
          searchResults.map((item) => (
            <div className="searchDropItem" key={item.id}>
              <Link 
                to={item.link} 
                className="searchDropLink" 
                state={{ product: { title: item.name, offerPrice: item.price, img: item.img } }} 
                onClick={() => {
                  setQ("");
                  setSearchFocused(false);
                  setMobileSearchOpen(false);
                  setMobileOpen(false);
                }}
              >
                <img src={item.img} alt={item.name} />
                <div className="searchDropItemText">
                  <span className="searchDropItemTitle">{item.name}</span>
                  <span className="searchDropItemPrice">₹{item.price}</span>
                </div>
              </Link>
            </div>
          ))
        ) : (
          <div className="searchDropMsg">
            No products found for "{q}"
            <button className="searchDropFallbackBtn" onClick={(e) => {
              e.preventDefault();
              handleSearch();
            }}>
              Search catalog
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="navWrap">
      <div className="navInner">
        {/* LEFT: Logo */}
        <div className="navLeft">
          <Link to="/" className="brand" aria-label="Ayrine Home">
            <img className="brandLogo" src={logo} alt="Ayrine Luxe" />
          </Link>
        </div>

        {/* CENTER: Desktop Links (dynamic from Dashboard) */}
        <nav className="navCenter navDesktop" aria-label="Primary">
          <NavLink to="/" className={linkClass}>
            Home
          </NavLink>
          {navCategories.map((cat) => {
            const { name, slug } = cat;
            const to = MAIN_CATEGORY_ROUTES[name] || `/category/${slug}`;
            return (
              <NavLink key={name} to={to} className={linkClass}>
                {name}
              </NavLink>
            );
          })}
          <NavLink to="/contact" className={linkClass}>
            Contact
          </NavLink>
        </nav>

        {/* ✅ Desktop Search Pill */}
        <div className="navSearch" aria-label="Navbar search">
          <div className="searchPill">
            <span className="searchIcon" aria-hidden="true">
              <FiSearch />
            </span>

            <div className="searchInputWrap">
              <input
                className="searchInput"
                value={q}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                aria-label="Search products"
              />

              {!q && (
                <div className="rotatingPh" aria-hidden="true">
                  <span key={animKey} className="phLine">
                    Search “{placeholderItems[idx]}”
                  </span>
                </div>
              )}
              {renderSearchDropdown()}
            </div>
          </div>
        </div>

        {/* RIGHT: Icons + Hamburger */}
        <div className="navRight">
          {/* ✅ Mobile/Tablet Expandable Search */}
          <div
            ref={mobileSearchRef}
            className={`mobileSearchWrap ${mobileSearchOpen ? "open" : ""}`}
          >
            <button
              className="iconBtn mobileOnly"
              type="button"
              title="Search"
              aria-label="Search"
              aria-expanded={mobileSearchOpen}
              onClick={() => setMobileSearchOpen((v) => !v)}
            >
              <FiSearch />
            </button>

            <div className="mobileExpandSearch" aria-label="Search expanded">
              <span className="searchIcon" aria-hidden="true">
                <FiSearch />
              </span>

              <div className="searchInputWrap">
                <input
                  ref={mobileInputRef}
                  className="searchInput"
                  value={q}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  aria-label="Search products"
                />

                {!q && (
                  <div className="rotatingPh" aria-hidden="true">
                    <span key={animKey} className="phLine">
                      Search “{placeholderItems[idx]}”
                    </span>
                  </div>
                )}
                {renderSearchDropdown()}
              </div>
            </div>
          </div>

          {/* ✅ WISHLIST (Hidden on Mobile) */}
          <Link to="/wishlist" className="navIconItem hideOnMobile" aria-label="Wishlist">
            <span className="iconBtn cartBtn" title="Wishlist">
              <FiHeart />
              {wishCount > 0 && (
                <span className="cartCountBadge">{wishCount}</span>
              )}
            </span>
            <span className="navIconLabel">Wishlist</span>
          </Link>

          {/* ✅ CART */}
          <Link to="/cart" className="navIconItem" aria-label="Cart">
            <span className="iconBtn cartBtn" title="Cart">
              <FiShoppingCart />
              {cartCount > 0 && (
                <span className="cartCountBadge">{cartCount}</span>
              )}
            </span>
            <span className="navIconLabel">Cart</span>
          </Link>

          {/* ✅ ORDERS (Hidden on Mobile) */}
          {user && (
            <Link to="/orders" className="navIconItem hideOnMobile" aria-label="Orders">
              <span className="iconBtn" title="Orders">
                <FiShoppingBag />
              </span>
              <span className="navIconLabel">Orders</span>
            </Link>
          )}

          {/* ✅ ACCOUNT PROFILE (Hidden on Mobile) */}
          {user ? (
            <Link to="/profile" className="navIconItem hideOnMobile" aria-label="My Profile">
              <span className="iconBtn" title="My Profile">
                <FiUser />
              </span>
              <span className="navIconLabel navUserLabel">
                {user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split("@")[0] || "Account"}
              </span>
            </Link>
          ) : (
            <button
              className="navIconItem hideOnMobile"
              type="button"
              aria-label="Sign in"
              onClick={openAuth}
            >
              <span className="iconBtn" title="Sign in">
                <FiUser />
              </span>
              <span className="navIconLabel">Sign in</span>
            </button>
          )}

          <button
            className="hamburgerBtn"
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <div
        className={`mobileDrawer ${mobileOpen ? "open" : ""}`}
        role="dialog"
        aria-label="Mobile menu"
      >
        <nav className="mobileNav" aria-label="Mobile Primary">
          <NavLink to="/" className={linkClass}>
            Home
          </NavLink>
          {navCategories.map((cat) => {
            const { name, slug } = cat;
            const to = MAIN_CATEGORY_ROUTES[name] || `/category/${slug}`;
            return (
              <NavLink key={name} to={to} className={linkClass}>
                {name}
              </NavLink>
            );
          })}
          <NavLink to="/contact" className={linkClass}>
            Contact
          </NavLink>
        </nav>

        <div className="mobileIconsRow">
          <Link to="/wishlist" className="mobileIconBtn" aria-label="Wishlist">
            <FiHeart /> <span>Wishlist</span>
            {wishCount > 0 && (
              <span className="cartCountBadge mobile">{wishCount}</span>
            )}
          </Link>

          <Link to="/cart" className="mobileIconBtn" aria-label="Cart">
            <FiShoppingCart /> <span>Cart</span>
            {cartCount > 0 && (
              <span className="cartCountBadge mobile">{cartCount}</span>
            )}
          </Link>

          {/* ✅ ORDERS & PROFILE */}
          {user && (
            <>
              <Link to="/orders" className="mobileIconBtn" aria-label="Orders">
                <FiShoppingBag /> <span>Orders</span>
              </Link>
              <Link to="/profile" className="mobileIconBtn" aria-label="Profile">
                <FiUser /> <span>Profile</span>
              </Link>
            </>
          )}

          {/* ✅ Mobile Account opens popup or logout */}
          {user ? (
            <button
              className="mobileIconBtn"
              type="button"
              aria-label="Sign out"
              onClick={() => { signOut(); setMobileOpen(false); }}
            >
              <FiLogOut /> <span>Sign out</span>
            </button>
          ) : (
            <button
              className="mobileIconBtn"
              type="button"
              aria-label="Login / Account"
              onClick={openAuth}
            >
              <FiUser /> <span>Account</span>
            </button>
          )}
        </div>
      </div>

      {/* Backdrop */}
      {mobileOpen && (
        <div className="backdrop" onClick={() => setMobileOpen(false)} />
      )}

      {/* ✅ Auth Popup */}
      {authOpen && <AuthPopup onClose={closeAuth} />}
    </header>
  );
}